import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import get from 'lodash/get';
import { notification } from 'antd';
import { pending, rejected } from 'redux-saga-thunk';
import { compose } from 'redux';
import CampaignList from '../../components/pages/CampaignList/index';
import { modalShow } from '../../store/modal/actions';
import {
  resourceCreateRequest,
  resourceUpdateRequest,
  resourceListReadRequest,
} from '../../store/resource/actions';
import { fromEntities, fromResource } from '../../store/selectors';
import { authLogout } from '../../store/auth/actions';
import CampaignsAdd from './CampaignsAdd';
import { StatusLabel } from '../../services/helper/index';

class CampaignListContainer extends Component {
  static defaultProps = {
    campaignList: [],
    advertiserList: {},
    publisherList: {},
    userList: [],
  };

  static propTypes = {
    campaignList: PropTypes.arrayOf(
      PropTypes.shape({
        paused: PropTypes.number,
        running: PropTypes.number,
        completed: PropTypes.number,
        advertiser_uuid: PropTypes.string,
        create_dtime: PropTypes.string,
        description: PropTypes.string,
        id: PropTypes.number,
        name: PropTypes.string,
        seller_pub_uuid: PropTypes.string,
        seller_pub_uuid_type: PropTypes.number,
        update_dtime: PropTypes.string,
      })
    ),
    publisherList: PropTypes.objectOf(
      PropTypes.shape({
        create_dtime: PropTypes.string,
        email: PropTypes.string,
        uuid: PropTypes.string,
        name: PropTypes.string,
        update_dtime: PropTypes.string,
      })
    ),
    advertiserList: PropTypes.objectOf(
      PropTypes.shape({
        uuid: PropTypes.string,
        name: PropTypes.string,
        description: PropTypes.string,
        private_key: PropTypes.string,
        update_dtime: PropTypes.string,
        create_dtime: PropTypes.string,
      })
    ),
    userList: PropTypes.arrayOf(
      PropTypes.shape({
        uuid: PropTypes.string,
        name: PropTypes.string,
      })
    ),
    logout: PropTypes.func.isRequired,
    getCampaignList: PropTypes.func.isRequired,
  };

  state = {
    archived: false,
    pagination: { current: 1, pageSize: 10, total: 0 },
    tableState: {
      sortField: null,
      sortOrder: null,
      searchTerm: null,
    },
    selectedRowKeys: [],
  };

  componentDidMount() {
    this.fetchTableData();
  }

  onSelectedRowKeysChange = selectedRowKeys => {
    this.setState({ selectedRowKeys });
  };

  getStatusLabel = record => {
    const statuses = ['Running', 'Paused', 'Completed', 'Scheduled'];

    return statuses.map(status => {
      if (record[status.toLowerCase()] > 0) {
        return (
          <StatusLabel key={status} record={record} status={status}>
            {record[status.toLowerCase()]}{' '}
          </StatusLabel>
        );
      }

      return false;
    });
  };

  fetchTableData() {
    this.props
      .getCampaignList({
        ...this.state.pagination,
        ...this.state.tableState,
        archived: this.state.archived,
      })
      .then(() =>
        this.setState({
          pagination: { ...this.state.pagination, total: this.props.campaignCount },
        })
      )
      .catch(e => {
        notification.error({
          message: 'Error',
          description: typeof e === 'string' ? e : 'Could not retrieve campaign list.',
          duration: 0,
        });
      });
  }

  handleArchiveCampaigns = () => this.updateStatus(3);

  handlePauseCampaigns = () => this.updateStatus(2);

  handleRunCampaigns = () => this.updateStatus(1);

  handleSearchTable(q) {
    // Clear sorting, pagination and search.
    this.setState(
      {
        pagination: { current: 1, pageSize: 10, total: 0 },
        tableState: {
          sortField: null,
          sortOrder: null,
          searchTerm: q,
        },
      },
      () => {
        this.fetchTableData();
      }
    );
  }

  handleTableChange = (pagination, filters, sorter) => {
    const pager = {
      ...this.state.pagination,
      current: pagination.current,
      total: this.props.campaignCount,
    };

    // Update state and fetch table data, pagination and sorter.  Filters are handled by search.
    const updateTableState = this.state.tableState;

    updateTableState.filteredInfo = filters;

    if (sorter) {
      updateTableState.sortField = sorter.columnKey;
      updateTableState.sortOrder = sorter.order;
    }

    this.setState({ pagination: pager, tableState: updateTableState }, () => {
      this.fetchTableData();
    });
  };

  toggleArchived = () => this.setState({ archived: !this.state.archived }, this.fetchTableData);

  updateStatus = statusId => {
    if (!this.state.selectedRowKeys.length) {
      notification.warn({
        message: 'Warning',
        description: 'Select a few rows first.',
        duration: 0,
      });
      return;
    }

    this.props
      .updateCampaignStatus(statusId, this.state.selectedRowKeys)
      .then(() => {
        notification.success({
          message: 'Success',
          description: 'Status successfully changed.',
          duration: 4,
        });
        this.setState({ selectedRowKeys: [] });
        this.fetchTableData();
      })
      .catch(e => {
        notification.error({
          message: 'Error',
          description: typeof e === 'string' ? e : 'Could not submit status change.',
          duration: 0,
        });
      });
  };

  render() {
    const { selectedRowKeys, pagination, archived } = this.state;
    const disableTableActions = !selectedRowKeys.length;
    const columns = [
      {
        title: 'Id',
        dataIndex: 'id',
      },
      {
        title: 'Name',
        defaultSortOrder: 'ascend',
        render: (text, record) => (
          <a
            onClick={e => {
              e.preventDefault();
              this.props.history.push(`/campaigns/${record.id}`);
            }}
            href={`/campaigns/${record.id}`}
          >
            {text}
          </a>
        ),
        dataIndex: 'name',
      },
      {
        title: 'Status',
        dataIndex: 'status',
        render: (text, record) => this.getStatusLabel(record),
      },
      {
        title: 'Advertiser',
        dataIndex: 'advertiser_uuid',
        render: text => get(this.props.advertiserList, [text, 'name'], text),
      },
      {
        title: 'Seller (Publisher)',
        dataIndex: 'seller_pub_uuid',
        render: text => get(this.props.publisherList, [text, 'name'], text),
      },
      {
        title: 'Impressions',
        dataIndex: 'impressions',
      },
    ];

    return [
      <CampaignsAdd key={1} handleCreateCampaign={this.props.createCampaign} />,
      <CampaignList
        key={2}
        history={this.props.history}
        archived={archived}
        toggleArchived={this.toggleArchived}
        pagination={pagination}
        tableData={this.props.campaignList}
        campaignCount={this.props.campaignCount}
        columns={columns}
        handlePauseCampaigns={this.handlePauseCampaigns}
        handleRunCampaigns={this.handleRunCampaigns}
        handleArchiveCampaigns={this.handleArchiveCampaigns}
        handleSearchTable={searchTerm => this.handleSearchTable(searchTerm)}
        showNewCampaignModal={this.props.showNewCampaignModal}
        handleTableChange={this.handleTableChange}
        loading={this.props.loading}
        disableTableActions={disableTableActions}
        selectedRowKeys={selectedRowKeys}
        onSelectedRowKeysChange={this.onSelectedRowKeysChange}
      />,
    ];
  }
}

const mapStateToProps = (
  state,
  {
    match: {
      params: { campaignId },
    },
  }
) => ({
  loading:
    pending(state, 'advertiserCampaignListRead') ||
    pending(state, 'advertiserCampaignStatusUpdate'),
  rejected: rejected(state, 'advertiserCampaignListRead'),
  campaignList: fromEntities.getList(
    state,
    'advertiserCampaign',
    fromResource.getList(state, 'advertiserCampaign')
  ),
  adList: fromEntities.getList(state, 'advertiserAd', campaignId),
  advertiserList: state.entities.advertiser || {},
  publisherList: state.entities.publisher || {},
  campaignCount: get(state, 'resource.advertiserCampaign.count', 0),
});

const mapDispatchToProps = dispatch => ({
  logout: () => dispatch(authLogout()),
  updateCampaignStatus: (statusId, campaignIds) =>
    dispatch(
      resourceUpdateRequest('advertiser-campaign/status/:statusId', { statusId }, campaignIds)
    ),
  createCampaign: data => dispatch(resourceCreateRequest('advertiser-campaign', {}, data)),
  getCampaignList: ({ searchTerm, current, pageSize, archived }) =>
    dispatch(
      resourceListReadRequest('advertiser-campaign', {
        q: searchTerm,
        page_num: current,
        page_size: pageSize,
        archived: !archived ? undefined : true,
      })
    ),
  showNewCampaignModal: () => dispatch(modalShow('add-campaign')),
});

export default compose(
  connect(
    mapStateToProps,
    mapDispatchToProps
  )
)(CampaignListContainer);
